import type { ClientPrincipalResponse } from '~/types/Auth'
const ENABLE_AGREEMENT_CHECK = true // TODO: a feature flag to enable/disable feature between environments

export const useAuthentication = () => {
  const { $i18n } = useNuxtApp()
  const refreshing = ref(false)
  const authStore = useAuthStore()
  const { isLocal } = useEnvironment()
  const localePath = useLocalePath()

  const fetchClientPrincipal = async () => {
    try {
      const response = await $fetch<ClientPrincipalResponse>('/.auth/me')
      const agreementClaim = response.clientPrincipal?.claims.find(
        (claim) => claim.typ === 'extension_Terms',
      )
      const hasAgreementAccepted = !agreementClaim || agreementClaim.val === 'True'
      if (!authStore.alreadyAgreed && ENABLE_AGREEMENT_CHECK)
        authStore.updateAgreementAccepted(import.meta.dev ? true : hasAgreementAccepted)
      return response
    } catch (error) {
      console.error(error)
      return {
        clientPrincipal: null,
      }
    }
  }

  // SWA answers /.auth/me with `clientPrincipal: null` for anonymous visitors, so public
  // pages can check for a login session without the 401 from /api/users/current.
  // Unlike fetchClientPrincipal, this leaves the terms-of-use state alone.
  const hasClientPrincipal = async () => {
    try {
      const response = await $fetch<ClientPrincipalResponse>('/.auth/me')
      return response?.clientPrincipal != null
    } catch (error) {
      console.error(error)
      return false
    }
  }

  const sessionExpired = computed(() => {
    return !authStore.isLoggedIn || !authStore.correlationId
  })

  const isPublicRoute = computed(() => {
    const normalizePath = localePath(useRoute().path, 'en')
    return useRoute().meta.environment === 'public' || normalizePath.startsWith('/public')
  })

  const isExpiredSession = computed(
    () => sessionExpired.value && !isPublicRoute.value && !isLocal.value,
  )

  const refreshAuthUser = async (throwError = true) => {
    try {
      refreshing.value = true
      await authStore.fetchLoggedInUser()
      if (!authStore.isHavingCorrectRoles() && throwError) {
        throw createError({
          statusCode: 403,
          message: $i18n.t('error.message.forbidden'),
        })
      }
    } catch (error) {
      console.error(error)
      if (throwError) {
        const statusCode =
          (error as { statusCode?: number; status?: number; response?: { status?: number } })
            ?.statusCode ??
          (error as { status?: number })?.status ??
          (error as { response?: { status?: number } })?.response?.status

        if (statusCode === 401) {
          navigateToLogin(useRoute().fullPath)
          return
        }

        if (statusCode === 403) {
          throw createError({
            statusCode: 403,
            message: (error as { message?: string })?.message ?? $i18n.t('error.message.forbidden'),
          })
        }

        if (statusCode) {
          throw createError({
            statusCode,
            message: $i18n.t('error.message.serverError'),
          })
        }

        throw createError({
          statusCode: 403,
          message: $i18n.t('error.message.unauthorized'),
        })
      }
    } finally {
      refreshing.value = false
    }
  }

  const loginRedirect = (path: string) => {
    navigateToLogin(path)
  }

  const handleExpiredSession = async (path: string) => {
    if (import.meta.dev) {
      await refreshAuthUser()
    } else {
      return loginRedirect(path)
    }
  }

  return {
    fetchClientPrincipal,
    hasClientPrincipal,
    sessionExpired,
    refreshAuthUser,
    loginRedirect,
    handleExpiredSession,
    refreshing,
    isPublicRoute,
    isExpiredSession,
  }
}
